import { Paragraph, Heading, Button } from '@expressable/syntax-ds';
import { FC, useState } from 'react';
import { useContentfulTherapist } from 'components/ContentfulTherapistCard/useContentfulTherapist';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

type Props = {
  therapistName: string;
};

export const AboutTheTherapist: FC<Props> = ({ therapistName }) => {
  const { data: therapistData } = useContentfulTherapist(therapistName);
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const formatLanguages = (langs: string[]) => {
    if (langs.length === 0) return '';

    if (langs.length === 1) return langs[0];

    return langs.join(', ');
  };

  if (!therapistData) return null;

  const languages = therapistData.fields.languagesSpoken ?? [];
  const languagesSpoken = formatLanguages(languages);

  return (
    <div>
      {!isOpen && (
        <>
          <Heading level="h6" noDefaultMargins className="mb-e8">
            {t('directSignup.eval.schedule.providerProfile.aboutMe')}
          </Heading>
          <Paragraph noDefaultMargins className="mb-e8 line-clamp-2">
            {therapistData.fields.about}
          </Paragraph>
          <Button variant="link" className="justify-start" onClick={toggleAccordion}>
            <Heading level="h6" noDefaultMargins className="text-purple-900">
              {t('directSignup.eval.schedule.providerProfile.readMore')}
            </Heading>
            <span className="flex-shrink-0">
              <CaretDown size={24} className="text-purple-900" />
            </span>
          </Button>
        </>
      )}
      {isOpen && (
        <>
          <div>
            <Heading level="h6" noDefaultMargins className="mb-e8">
              {t('directSignup.eval.schedule.providerProfile.aboutMe')}
            </Heading>
            <Paragraph>{therapistData.fields.about}</Paragraph>
            <Heading level="h6" noDefaultMargins className="mb-e8">
              {t('directSignup.eval.schedule.providerProfile.educationAndtraining')}
            </Heading>
            <div className="mb-16">
              {therapistData.fields.educationAndTraining?.length &&
                therapistData.fields.educationAndTraining.map((education, index) => (
                  <Paragraph key={index} noDefaultMargins>
                    {education}
                  </Paragraph>
                ))}
            </div>
            <Heading level="h6" noDefaultMargins className="mb-e8">
              {t('directSignup.eval.schedule.providerProfile.languagesSpoken')}
            </Heading>
            <Paragraph>{languagesSpoken || t('directSignup.eval.schedule.providerProfile.defaultLanguage')}</Paragraph>
            <Heading level="h6" noDefaultMargins className="mb-e8">
              {t('directSignup.eval.schedule.providerProfile.otherInterests')}
            </Heading>
            <Paragraph className="mb-24">{therapistData.fields.interests}</Paragraph>
            {therapistData.fields.preferredName && (
              <>
                <Heading level="h6" noDefaultMargins className="mb-e8">
                  {t('directSignup.eval.schedule.providerProfile.preferredName')}
                </Heading>
                <Paragraph className="mb-24">{therapistData.fields.preferredName}</Paragraph>
              </>
            )}
            {therapistData.fields.pronouns && (
              <>
                <Heading level="h6" noDefaultMargins className="mb-e8">
                  {t('directSignup.eval.schedule.providerProfile.pronouns')}
                </Heading>
                <Paragraph className="mb-24">{therapistData.fields.pronouns}</Paragraph>
              </>
            )}
          </div>
          <Button variant="link" className="justify-start" onClick={toggleAccordion}>
            <Heading level="h6" noDefaultMargins className="text-purple-900">
              {t('directSignup.eval.schedule.providerProfile.readLess')}
            </Heading>
            <span className="flex-shrink-0">
              <CaretUp size={24} className="text-purple-900" />
            </span>
          </Button>
        </>
      )}
    </div>
  );
};
